import {
  buildBrand,
  alpha,
  formatDate,
  getGrade,
  getGradeColor,
  slugify,
  stripMarkdownText,
  classifyAsset,
  getAssetTypeLabel,
} from './externalReportPdfHelpers';

export { alpha, getAssetTypeLabel };

export const VERIFY_THRESHOLD = 25;

export const SCORE_COLORS = {
  likely: '#C62828',
  possible: '#EF6C00',
  watchlist: '#1565C0',
  low: '#5B6B82',
};

export const EXTERNAL_RECON_METHODOLOGY_PARAGRAPHS = [
  'We begin each recon-focused external assessment by confirming the authorized scope and building a working view of the internet-facing attack surface. The assessment team reviews the provided targets, correlates them with passive intelligence, and identifies domains, subdomains, hosts, and infrastructure that may represent reachable perimeter exposure.',
  'After the initial scope review, we perform controlled discovery and enumeration to determine which assets are reachable from the public internet. This includes DNS review, host discovery, port scanning, service identification, and fingerprinting of exposed software and supporting technologies. The goal is to understand what is actually visible before drawing conclusions about plausible attack paths.',
  'As exposed services are identified, we normalize the observed ports, protocols, service names, and technology indicators into an asset inventory. The assessment team then uses that inventory to reason about likely trust boundaries, externally reachable entry points, administrative surfaces, legacy services, and technologies that may require deeper validation during a full assessment.',
  'The threat-modeling phase translates that recon data into attack hypotheses for each asset. We consider the exposed services, detected technologies, and asset context to identify plausible paths such as service misconfiguration, outdated software exposure, authentication weaknesses, remote access risks, and other perimeter concerns. This report is intended to show what was mapped, why each asset matters, and which threats appear most plausible based on observed external exposure.',
];

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeHost = (value, fallback = 'Unknown asset') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;

  try {
    return new URL(raw).hostname || raw;
  } catch {
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '')
      .trim() || fallback;
  }
};

const normalizeLookupKey = (value) => normalizeHost(value, '').toLowerCase();

const cleanToken = (value) => {
  const cleaned = String(value ?? '')
    .replace(/^['"`]+|['"`]+$/g, '')
    .replace(/:+$/g, '')
    .trim();

  if (!cleaned) return '';
  if (cleaned === '[object Object]') return '';
  if (cleaned.toLowerCase() === 'null' || cleaned.toLowerCase() === 'undefined') return '';
  return cleaned;
};

const safeParseList = (value) => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return [...new Set(value.map(cleanToken).filter(Boolean))];
  }

  if (typeof value === 'object') {
    return [...new Set(Object.values(value).flatMap((entry) => safeParseList(entry)))];
  }

  const raw = String(value || '').trim();
  if (!raw) return [];

  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return safeParseList(parsed);
    } catch {
      // fall through
    }
  }

  if (raw.includes(',')) {
    return [...new Set(raw.split(',').map(cleanToken).filter(Boolean))];
  }

  const token = cleanToken(raw);
  return token ? [token] : [];
};

const normalizeTechToken = (value) => {
  const token = cleanToken(value);
  if (!token) return '';
  if (!token.includes(':')) return token;

  const [leftRaw, rightRaw] = token.split(':', 2);
  const left = cleanToken(leftRaw);
  const right = cleanToken(rightRaw);
  const genericKeys = new Set([
    'server',
    'web server',
    'http server',
    'framework',
    'platform',
    'powered by',
    'technology',
    'stack',
    'vendor',
    'product',
    'service',
  ]);

  if (genericKeys.has(left.toLowerCase()) && right) return right;
  return left || token;
};

const compactText = (value, maxLength = 72) => {
  const cleaned = stripMarkdownText(value);
  if (!cleaned) return '';
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 3).trim()}...` : cleaned;
};

const cleanParagraphs = (value, fallback = []) => {
  const paragraphs = String(value || '')
    .split(/\n{2,}/g)
    .map((paragraph) => stripMarkdownText(paragraph))
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.length ? paragraphs : fallback;
};

const softWrapText = (value) =>
  String(value || '').replace(/([/_?&=.:.-])/g, '$1\u200b');

export const scoreLabel = (score) => {
  const numeric = num(score);
  if (numeric >= 75) return 'Likely';
  if (numeric >= 50) return 'Possible';
  if (numeric >= VERIFY_THRESHOLD) return 'Watchlist';
  return 'Low';
};

export const scoreColor = (score) => {
  const label = scoreLabel(score).toLowerCase();
  return SCORE_COLORS[label] || SCORE_COLORS.low;
};

const summarizeAsset = (asset) => {
  const description = stripMarkdownText(asset.description || '');
  if (description) return description;

  const parts = [];
  if (asset.ports.length) parts.push(`${asset.ports.length} open port${asset.ports.length === 1 ? '' : 's'}`);
  if (asset.services.length) parts.push(`${asset.services.length} observed service${asset.services.length === 1 ? '' : 's'}`);
  if (asset.technologies.length) parts.push(`${asset.technologies.length} technology indicator${asset.technologies.length === 1 ? '' : 's'}`);

  if (parts.length) {
    return `The assessment team observed ${parts.join(', ')} on this externally reachable asset. This asset was included in the threat-model review because the exposed surface may influence likely perimeter attack paths.`;
  }

  return 'This externally scoped asset was included in the recon inventory, but no additional service or technology details were available in the current dataset.';
};

const buildExecutiveSummary = ({
  totalAssets,
  uniquePorts,
  uniqueServices,
  uniqueTechnologies,
  assetsWithAttacks,
  totalHypotheses,
  totalLikelyAttacks,
  topRiskAsset,
}) => {
  const coverage = totalAssets ? Math.round((assetsWithAttacks / totalAssets) * 100) : 0;

  return [
    `The assessment team mapped ${totalAssets} externally reachable asset${totalAssets === 1 ? '' : 's'} and normalized the observed perimeter exposure into host-level recon records. Across that inventory, we observed ${uniquePorts} unique open port${uniquePorts === 1 ? '' : 's'}, ${uniqueServices} unique service${uniqueServices === 1 ? '' : 's'}, and ${uniqueTechnologies} technology indicator${uniqueTechnologies === 1 ? '' : 's'}.`,
    `Threat modeling produced ${totalHypotheses} attack hypothesis${totalHypotheses === 1 ? '' : 'es'} across ${assetsWithAttacks} asset${assetsWithAttacks === 1 ? '' : 's'}, representing ${coverage}% of the mapped external inventory. ${totalLikelyAttacks} hypothesis${totalLikelyAttacks === 1 ? '' : 'es'} scored at or above the active verification threshold and should be treated as higher-priority review candidates.`,
    topRiskAsset
      ? `The highest-scoring asset in the current recon set was ${topRiskAsset.host}, with a top threat-model score of ${topRiskAsset.topAttackScore}. Its exposed services and technology indicators make it a strong candidate for follow-up validation during a full external assessment.`
      : 'No individual asset materially stood out above the rest of the current recon set. As additional enumeration data becomes available, the model can be refreshed to refine prioritization.',
    'This report is focused on reconnaissance and threat modeling only. It is intended to help stakeholders understand what was externally visible, how that exposure was grouped by asset, and which attack paths appear most plausible based on the observed perimeter data.',
  ];
};

export const buildExternalReconReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  stats,
  assets,
  threatModel,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');
  const assetInventory = Array.isArray(assets) ? assets : assets?.assets || [];
  const threatEndpoints = Array.isArray(threatModel)
    ? threatModel
    : threatModel?.endpoints || threatModel?.threat_model?.endpoints || [];

  const map = new Map();

  const ensureAsset = (host, seed = {}) => {
    const normalizedHost = normalizeHost(host, `Asset ${map.size + 1}`);
    const key = normalizeLookupKey(normalizedHost);

    if (!map.has(key)) {
      const classification = classifyAsset(normalizedHost);
      map.set(key, {
        key,
        host: normalizedHost,
        assetType: classification.type,
        assetTypeLabel: getAssetTypeLabel(classification.type),
        description: '',
        ports: [],
        services: [],
        technologies: [],
        attacks: [],
        threatNarrative: '',
        sourceCount: 0,
        ...seed,
      });
    }

    return map.get(key);
  };

  assetInventory.forEach((asset, index) => {
    const host = asset?.host || asset?.asset || asset?.domain || asset?.ip || `Asset ${index + 1}`;
    const entry = ensureAsset(host);
    const description = stripMarkdownText(asset?.description || '');

    if (description && description.length > entry.description.length) {
      entry.description = description;
    }

    entry.ports = [...new Set([...entry.ports, ...safeParseList(asset?.ports)])];
    entry.services = [...new Set([...entry.services, ...safeParseList(asset?.services)])];
    entry.technologies = [
      ...new Set([
        ...entry.technologies,
        ...safeParseList(asset?.technologies).map(normalizeTechToken).filter(Boolean),
      ]),
    ];
    entry.sourceCount += 1;
  });

  threatEndpoints.forEach((endpoint, index) => {
    const host = endpoint?.path || endpoint?.host || endpoint?.asset || `Threat Asset ${index + 1}`;
    const entry = ensureAsset(host);
    const attacks = Array.isArray(endpoint?.threat_model?.attacks) ? endpoint.threat_model.attacks : [];

    entry.threatNarrative = endpoint?.threat_model?.threat_model || entry.threatNarrative;
    entry.attacks = [...attacks]
      .sort((left, right) => num(right?.score) - num(left?.score))
      .map((attack, attackIndex) => ({
        id: `${slugify(`${entry.host}-${attack?.name || `attack-${attackIndex + 1}`}`)}-${attackIndex + 1}`,
        name: stripMarkdownText(attack?.name || `Attack ${attackIndex + 1}`),
        score: num(attack?.score),
        label: scoreLabel(attack?.score),
        color: scoreColor(attack?.score),
        verified: num(attack?.score) >= VERIFY_THRESHOLD,
        ports: safeParseList(attack?.ports),
        services: safeParseList(attack?.service || attack?.services),
        description: cleanParagraphs(
          attack?.reason || attack?.description || attack?.details || attack?.rationale || '',
          ['No supporting attack narrative was provided for this hypothesis.']
        ),
      }));

    entry.ports = [...new Set([...entry.ports, ...entry.attacks.flatMap((attack) => attack.ports)])];
    entry.services = [...new Set([...entry.services, ...entry.attacks.flatMap((attack) => attack.services)])];
  });

  const assetRows = Array.from(map.values())
    .map((asset) => {
      const topAttackScore = asset.attacks.reduce((max, attack) => Math.max(max, attack.score), 0);
      const likelyAttackCount = asset.attacks.filter((attack) => attack.verified).length;
      const exposureCount = asset.ports.length + asset.services.length + asset.technologies.length;
      const summaryText = summarizeAsset(asset);

      return {
        ...asset,
        title: asset.host,
        wrappedTitle: softWrapText(asset.host),
        wrappedHost: softWrapText(asset.host),
        summaryText,
        compactTitle: compactText(asset.host, 58),
        topAttackScore,
        likelyAttackCount,
        totalAttackCount: asset.attacks.length,
        exposureCount,
      };
    })
    .sort((left, right) => {
      if (right.topAttackScore !== left.topAttackScore) return right.topAttackScore - left.topAttackScore;
      if (right.likelyAttackCount !== left.likelyAttackCount) return right.likelyAttackCount - left.likelyAttackCount;
      if (right.exposureCount !== left.exposureCount) return right.exposureCount - left.exposureCount;
      return left.host.localeCompare(right.host);
    })
    .map((asset, index) => ({
      ...asset,
      ordinal: index + 1,
      id: `asset-${index + 1}-${slugify(asset.host)}`,
    }));

  const uniquePorts = new Set(assetRows.flatMap((asset) => asset.ports)).size;
  const uniqueServices = new Set(assetRows.flatMap((asset) => asset.services)).size;
  const uniqueTechnologies = new Set(assetRows.flatMap((asset) => asset.technologies)).size;
  const totalAssets = assetRows.length;
  const assetsWithAttacks = assetRows.filter((asset) => asset.totalAttackCount > 0).length;
  const totalHypotheses = assetRows.reduce((sum, asset) => sum + asset.totalAttackCount, 0);
  const totalLikelyAttacks = assetRows.reduce((sum, asset) => sum + asset.likelyAttackCount, 0);
  const averageTopScore = totalAssets
    ? Math.round(assetRows.reduce((sum, asset) => sum + asset.topAttackScore, 0) / totalAssets)
    : 0;
  const topRiskAsset = assetRows[0] || null;
  const riskScore = stats?.risk_score ?? stats?.score ?? null;
  const grade = getGrade(riskScore);

  const topAssets = assetRows.slice(0, 5).map((asset) => ({
    id: asset.id,
    title: asset.title,
    wrappedTitle: asset.wrappedTitle,
    score: asset.topAttackScore,
    likelyAttackCount: asset.likelyAttackCount,
    totalAttackCount: asset.totalAttackCount,
    exposureCount: asset.exposureCount,
  }));

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    ...assetRows.map((asset) => ({
      id: asset.id,
      title: `Asset ${asset.ordinal}: ${asset.compactTitle}`,
      level: 1,
    })),
  ];

  return {
    pentestId,
    clientName: clientName || stats?.meta?.client_name || stats?.client || 'Client Name',
    companyName: companyName || 'StealthNet AI',
    reportDate: formatDate(stats?.meta?.generated_at || Date.now()),
    testingType: 'External Reconnaissance & Threat Modeling',
    organizationName: stats?.meta?.client_name || stats?.client || clientName || 'Client',
    brand,
    cover: {
      eyebrow: 'Recon Report',
      title: 'External Recon Report',
      subtitle: 'Asset Inventory and Threat Modeling',
      tags: ['Reconnaissance', 'Threat Modeling', 'External Attack Surface'],
    },
    grade,
    gradeColor: getGradeColor(grade),
    riskScore,
    executiveSummary: buildExecutiveSummary({
      totalAssets,
      uniquePorts,
      uniqueServices,
      uniqueTechnologies,
      assetsWithAttacks,
      totalHypotheses,
      totalLikelyAttacks,
      topRiskAsset,
    }),
    methodology: EXTERNAL_RECON_METHODOLOGY_PARAGRAPHS,
    assetRows,
    topAssets,
    tocItems,
    metrics: {
      totalAssets,
      uniquePorts,
      uniqueServices,
      uniqueTechnologies,
      assetsWithAttacks,
      totalHypotheses,
      totalLikelyAttacks,
      averageTopScore,
    },
  };
};
